/**
 * HVA ARTISTEN SER (sak 229.8–229.10, 231.1–231.3; 0452).
 *
 * Atle, 29. september: «Alt slikt skal ligge i bookingbyråets innstillinger under rettigheter.»
 * Byrået bestemmer i Rettigheter hva den innloggede artisten — og management som logger inn på
 * artistsiden (231.1) — ser på konsertsiden: én bryter per område.
 *
 * IKKE EN NY ROLLE. Artisten er ikke medlem av huset (216.2), og en rolle `artist` i `org_role`
 * ville gått gjennom hver rad i matrisen. Bryterne er en egen tabell, `artist_view_permissions`,
 * én rad per (hus, område), og håndheves i basen: `app.my_concert` utelater et avslått område,
 * `app.my_concert_deal` gir kontrakt og økonomi bare når de er slått på, og chat og oppgaver følger
 * cellen (0454). Nettsiden viser bare det basen har sendt.
 *
 * Standard: alt på unntatt kontrakt og økonomi (216.6–216.8: «alt unntatt økonomi og kontrakt»).
 */
import { z } from 'zod';

export const ARTIST_VIEW_AREAS = [
  'schedule',
  'people',
  'travel',
  'stay',
  'advance_technical',
  'advance_hospitality',
  'advance_promo',
  'advance_other',
  'deviations',
  'tasks',
  'documents',
  'chat',
  'contract',
  'economy',
] as const;
export type ArtistViewArea = (typeof ARTIST_VIEW_AREAS)[number];
export const artistViewAreaSchema = z.enum(ARTIST_VIEW_AREAS).meta({ id: 'ArtistViewArea' });

/** Det et nytt hus starter med. `generate-default-permissions.ts` skriver den til SQL. */
export const DEFAULT_ARTIST_VIEW: Readonly<Record<ArtistViewArea, boolean>> = {
  schedule: true,
  people: true,
  travel: true,
  stay: true,
  advance_technical: true,
  advance_hospitality: true,
  advance_promo: true,
  advance_other: true,
  deviations: true,
  tasks: true,
  documents: true,
  chat: true,
  contract: false,
  economy: false,
};

/**
 * Hvilket område en advance-seksjon hører til, ut fra malnøkkelen (`advance_sections.template_key`).
 * Den samme regelen står i `app.my_concert` (0453); en seksjon uten kjent nøkkel er «annet».
 */
export function advanceAreaForSection(templateKey: string | null | undefined): ArtistViewArea {
  if (templateKey === 'technical') return 'advance_technical';
  if (templateKey === 'hospitality') return 'advance_hospitality';
  if (templateKey === 'promo') return 'advance_promo';
  return 'advance_other';
}

/** Kartet i konsertdokumentet: hvert område, på eller av, slik byrået har satt det. */
export const artistViewAreasSchema = z
  .record(artistViewAreaSchema, z.boolean())
  .meta({ id: 'ArtistViewAreas' });
export type ArtistViewAreas = z.infer<typeof artistViewAreasSchema>;

export const artistViewSettingSchema = z.object({
  area: artistViewAreaSchema,
  allowed: z.boolean(),
  /**
   * MANAGEMENT SIN KOLONNE (sak 231.1). Verdien management får — lik `allowed` til byrået har satt
   * den selv. `managementFollowsArtist` sier om den fortsatt følger artisten. Valgfrie, så et API som
   * ikke er startet på nytt ennå ikke bryter kontrakten: da følger management artisten.
   */
  management: z.boolean().optional(),
  managementFollowsArtist: z.boolean().optional(),
});
export type ArtistViewSetting = z.infer<typeof artistViewSettingSchema>;

/** `GET`/`PUT /v1/settings/artist-view`: alle områdene, i fast rekkefølge (`ARTIST_VIEW_AREAS`). */
export const artistViewSettingsSchema = z
  .object({ items: z.array(artistViewSettingSchema) })
  .meta({ id: 'ArtistViewSettings' });
export type ArtistViewSettings = z.infer<typeof artistViewSettingsSchema>;

/** Én bryter. Svaret er hele lista, lest på nytt etter skrivingen. */
export const ARTIST_VIEW_AUDIENCES = ['artist', 'management'] as const;
export type ArtistViewAudience = (typeof ARTIST_VIEW_AUDIENCES)[number];

export const updateArtistViewRequestSchema = z
  .object({
    area: artistViewAreaSchema,
    allowed: z.boolean(),
    /** Hvilken kolonne (231.1). Uten den er det artistens, som før. */
    audience: z.enum(ARTIST_VIEW_AUDIENCES).optional(),
  })
  .meta({ id: 'UpdateArtistViewRequest' });
export type UpdateArtistViewRequest = z.infer<typeof updateArtistViewRequestSchema>;
